import { formatPercentageLabel } from '../../utils';
import { useState } from 'react';

interface RouteStatusMiniBarProps {
  completed: number;
  failed: number;
  pending: number;
  expired: number;
}

const COLORS = {
  completed: '#10B981',
  failed: '#EF4444',
  pending: '#3B82F6',
  expired: '#6B7280',
};

const RouteStatusMiniBar = ({
  completed,
  failed,
  pending,
  expired,
}: RouteStatusMiniBarProps) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const total = completed + failed + pending + expired;

  if (total <= 0) {
    return <div className='h-4 w-full min-w-[180px] rounded-md bg-gray-100' />;
  }

  const segments = [
    { key: 'completed', name: 'Completadas', value: completed, color: COLORS.completed, label: 'visitas' },
    { key: 'failed', name: 'Fallidas', value: failed, color: COLORS.failed, label: 'visitas' },
    { key: 'pending', name: 'Pendientes', value: pending, color: COLORS.pending, label: 'visitas' },
    { key: 'expired', name: 'Expiradas', value: expired, color: COLORS.expired, label: 'visitas' },
  ]
    .map((segment) => ({
      ...segment,
      label: segment.value === 1 ? 'visita' : 'visitas',
    }))
    .filter((segment) => segment.value > 0);

  return (
    <div className='flex h-4 w-full min-w-[180px] gap-1 rounded-md bg-gray-100'>
      {segments.map((segment, index) => {
        const percentage = (segment.value / total) * 100;
        const showInlineLabel = percentage >= 15;

        return (
          <div
            key={segment.key}
            onMouseEnter={() => setHoveredKey(segment.key)}
            onMouseLeave={() => setHoveredKey(null)}
            className={`relative flex h-full items-center justify-center ${
              index === 0 ? 'rounded-l-md' : ''
            } ${index === segments.length - 1 ? 'rounded-r-md' : ''}`}
            style={{
              backgroundColor: segment.color,
              flexBasis: `${percentage}%`,
            }}
          >
            {showInlineLabel && (
              <span className='text-xs font-semibold leading-none text-white'>
                {Math.round(percentage)}%
              </span>
            )}

            {hoveredKey === segment.key && (
              <div
                role='tooltip'
                className='pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-left shadow-lg'
              >
                <div className='flex items-center gap-1.5 text-xs font-semibold text-gray-800'>
                  <span
                    className='h-2 w-2 shrink-0 rounded-full'
                    style={{ backgroundColor: segment.color }}
                  />
                  {segment.name}
                </div>
                <div className='mt-0.5 text-xs text-gray-500'>
                  <span className='font-bold text-gray-900'>
                    {segment.value} {segment.label}
                  </span>{' '}
                  · {formatPercentageLabel(percentage)}%
                </div>
                <div className='absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-white' />
                <div className='absolute left-1/2 top-full -z-10 -translate-x-1/2 translate-y-[1px] border-4 border-transparent border-t-gray-200' />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RouteStatusMiniBar;
